// Water: ponds, streams and waterfalls, drawn exactly where the sim put them (sim/world.ts:
// ponds / streams / falls). The surface heights are the sim's, so what you skim is what you see.
//
// Three draw calls for all the water on an island, every animation in the shaders:
//   1. surfaces  - pond discs + stream ribbons in one mesh. Teal body that turns to reflected
//                  golden sky at grazing angles (so it reads as a bright ribbon from the gameplay
//                  camera), lighter shallow margins, cream foam lines along the banks and on the
//                  rapids, flow streaks scrolling down the stream, sun glints.
//   2. falls     - one curved sheet per waterfall (projectile arc off the lip), foam streaks that
//                  stretch as they accelerate, ragged translucent edges; edge falls dissolve into
//                  the haze far below.
//   3. mist      - camera-facing churn puffs at each lip, landing and down the long edge falls
//                  (billboarded in the vertex shader; no per-frame CPU work).
// Haze comes from materials.ts (withHaze), so far water melts into the gold like everything else.

import * as THREE from 'three';
import type { World, StreamSeg } from '../../../sim/world';
import { NO_GROUND } from '../../../sim/world';
import { HAZE, withHaze } from '../materials';
import { PlanarReflection } from './reflection';

// ------------------------------------------------------------------ shared noise texture
/** Tileable 4-channel value-noise fbm (R,G: surface normal xz; B: foam/streak; A: sparkle). */
function makeNoise(size = 128): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  const lattice = (seed: number, n: number) => {
    const a = new Float32Array(n * n);
    let s = seed;
    for (let i = 0; i < a.length; i++) { s = (s * 16807) % 2147483647; a[i] = s / 2147483647; }
    return a;
  };
  const sm = (t: number) => t * t * (3 - 2 * t);
  const ch = new Float32Array(size * size);
  for (let c = 0; c < 4; c++) {
    ch.fill(0);
    let amp = 0.5, tot = 0;
    for (let o = 0; o < 4; o++) {
      const n = 4 << o; // cells per tile
      const L = lattice(1013 + c * 7919 + o * 104729, n);
      for (let y = 0; y < size; y++) {
        const fy = (y / size) * n, iy = Math.floor(fy), ty = sm(fy - iy);
        const y0 = iy % n, y1 = (iy + 1) % n;
        for (let x = 0; x < size; x++) {
          const fx = (x / size) * n, ix = Math.floor(fx), tx = sm(fx - ix);
          const x0 = ix % n, x1 = (ix + 1) % n;
          const a = L[y0 * n + x0] + (L[y0 * n + x1] - L[y0 * n + x0]) * tx;
          const b = L[y1 * n + x0] + (L[y1 * n + x1] - L[y1 * n + x0]) * tx;
          ch[y * size + x] += (a + (b - a) * ty) * amp;
        }
      }
      tot += amp; amp *= 0.5;
    }
    // stretch the contrast back out (fbm piles up around 0.5)
    for (let i = 0; i < ch.length; i++) {
      const v = (ch[i] / tot - 0.5) * 2.1 + 0.5;
      data[i * 4 + c] = Math.max(0, Math.min(255, Math.round(v * 255)));
    }
  }
  const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.needsUpdate = true;
  return t;
}

// ------------------------------------------------------------------ pond depth map
/**
 * Water depth over every pond, baked once from the sim: pond level minus the carved bed
 * (world.terrain), and zero inside any landform/obstacle footprint that stands through the water
 * (the Lagoon's tower), so the shader can fade the shore, darken a wet band and ring the rocks.
 * 1 m cells, one tile per pond side by side in a single RG8 atlas (G = closeness to a rock in the water) (the Lagoon is ~56x56, the
 * whole island ~5k samples, a few ms). tiles[i] = (offU, offV, kU, kV): uv = off + worldXZ * k.
 * Encoded (depth + 0.5) / 3.5, so -0.5 m (dry bank) .. 3 m.
 */
function bakePondDepth(world: World): { tex: THREE.DataTexture; tiles: [number, number, number, number][] } {
  const res = 1, pad = 2;
  const dims = world.ponds.map((p) => Math.ceil((2 * (p.r + pad)) / res) + 1);
  const W = Math.max(1, dims.reduce((a, b) => a + b, 0)), H = Math.max(1, ...dims);
  const data = new Uint8Array(W * H * 2);
  const tiles: [number, number, number, number][] = [];
  let x0 = 0;
  world.ponds.forEach((p, pi) => {
    const n = dims[pi];
    const minX = p.x - (p.r + pad), minZ = p.z - (p.r + pad);
    // rocks standing in this pond
    const cyls = world.cyls.filter((c) => Math.hypot(c.x - p.x, c.z - p.z) < p.r + pad + c.r && c.y0 <= p.y + 0.1 && c.y1 >= p.y);
    const boxes = world.boxes.filter((b) => Math.abs(b.x - p.x) < p.r + pad + b.hx && Math.abs(b.z - p.z) < p.r + pad + b.hz && b.y0 <= p.y + 0.1 && b.y1 >= p.y);
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
      const x = minX + i * res, z = minZ + j * res;
      const g = world.terrain(x, z);
      let d = g === NO_GROUND ? 3 : p.y - g;
      // G: closeness to a rock standing in the water (1 at its face, 0 by 3 m out)
      let near = 9;
      for (const c of cyls) near = Math.min(near, Math.hypot(x - c.x, z - c.z) - c.r);
      for (const b of boxes) near = Math.min(near, Math.max(Math.abs(x - b.x) - b.hx, Math.abs(z - b.z) - b.hz));
      if (near < 0) d = Math.min(d, -0.3);
      const k = (j * W + x0 + i) * 2;
      data[k] = Math.max(0, Math.min(255, Math.round(((d + 0.5) / 3.5) * 255)));
      data[k + 1] = Math.round(255 * Math.max(0, Math.min(1, 1 - near / 3)));
    }
    const kU = 1 / (res * W), kV = 1 / (res * H);
    tiles.push([(x0 + 0.5) / W - minX * kU, 0.5 / H - minZ * kV, kU, kV]);
    x0 += n;
  });
  const tex = new THREE.DataTexture(data, W, H, THREE.RGFormat, THREE.UnsignedByteType);
  tex.magFilter = THREE.LinearFilter; tex.minFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return { tex, tiles };
}

// ------------------------------------------------------------------ shaders
const COMMON_FRAG = /* glsl */ `
uniform sampler2D uNoise;
uniform float uTime;
uniform vec3 uDeep; uniform vec3 uShallow; uniform vec3 uFoam; uniform vec3 uFar;
uniform vec3 uSkyTop; uniform vec3 uSkyHorizon;
uniform float uQ;
`;

const SURF_VERT = /* glsl */ `
#include <common>
attribute vec2 aUV;
attribute vec4 aP;
attribute vec4 aD;
varying vec2 vUV;
varying vec4 vP;
varying vec4 vD;
void main() {
  vUV = aUV; vP = aP; vD = aD;
  vec3 transformed = position;
  #include <project_vertex>
}
`;

const SURF_FRAG = /* glsl */ `
#include <common>
${COMMON_FRAG}
#if WQ >= 1
uniform sampler2D uRefl; uniform mat4 uReflMat; uniform float uReflY; uniform float uReflOn; uniform vec2 uReflPx;
#endif
uniform sampler2D uDepth;
varying vec4 vD;
varying vec2 vUV;
varying vec4 vP;
void main() {
  float edge = vP.x, froth = vP.z, kind = vP.w;
  // streams carry their speed in vP.y, ponds their radius: banks are measured in metres, so a
  // 26 m lake gets the same narrow foam line and ink edge as a 6 m pool
  float spd = kind > 0.5 ? vP.y : 0.0;
  float bankR = kind > 0.5 ? 3.0 : vP.y;
  float dB = ( 1.0 - edge ) * bankR; // metres in from the bank (negative past it)
  // ponds read their real water depth from the baked map (pond level minus the carved bed, zero
  // inside any rock standing in the water), so the shore follows the true waterline, around the
  // tower too, instead of the ideal circle; depth 0.4 m ~ a metre in from the bank
  float wd = 9.0, rockK = 0.0;
  if ( vD.z > 0.0 ) {
    vec2 dm = texture2D( uDepth, vD.xy + vUV * vD.zw ).rg;
    wd = dm.r * 3.5 - 0.5;
    rockK = dm.g;
    dB = min( dB, wd * 2.5 );
  }
  vec3 toFrag = vHazeWorld - cameraPosition;
  float dist = length( toFrag );
  vec3 V = toFrag / max( dist, 1e-3 );
  float farW = smoothstep( 50.0, 150.0, dist );
  float t = uTime;
  // flow-aligned coordinates: streams carry (across m, along m); ponds carry world xz
  vec2 a1, a2, sc;
  if ( kind > 0.5 ) {
    a1 = vec2( vUV.x * 0.26, ( vUV.y - t * spd ) * 0.12 );
    a2 = vec2( vUV.x * 0.55 + 0.31, ( vUV.y - t * spd * 1.4 ) * 0.27 );
    sc = vec2( vUV.x * 0.42, ( vUV.y - t * spd * 1.1 ) * 0.022 );
  } else {
    a1 = vUV * 0.13 + t * vec2( 0.012, 0.018 );
    a2 = vUV * 0.29 + vec2( 0.31 ) - t * vec2( 0.022, 0.008 );
    sc = vUV * 0.06 + t * vec2( 0.004, -0.003 );
  }
  vec4 n1 = texture2D( uNoise, a1 );
  vec4 n2 = texture2D( uNoise, a2 );
  // soft wind ripples: a slanted normal from two scrolling noise layers (stronger on moving water)
  float rough = 0.16 + 0.24 * clamp( spd * 0.4, 0.0, 1.0 ) + froth * 0.4;
  vec3 N = normalize( vec3( ( n1.r - 0.5 ) + ( n2.g - 0.5 ) * 0.8, 1.0 / rough, ( n1.g - 0.5 ) + ( n2.r - 0.5 ) * 0.8 ) );
  vec3 R = reflect( V, N );
  float cosT = clamp( -dot( V, N ), 0.0, 1.0 );
  // painterly fresnel: from the gameplay camera most of the ribbon mirrors the golden sky
  float fres = 0.06 + 0.7 * pow( 1.0 - cosT, 4.0 );
  float sunR = max( dot( R, uSunDir ), 0.0 );
  // stillness: ponds are still; streams by their speed, so slow stretches mirror and rapids don't
  float still = kind > 0.5 ? ( 1.0 - smoothstep( 1.15, 2.1, spd ) ) * ( 1.0 - smoothstep( 0.0, 0.12, froth ) ) : 1.0;
  // analytic sky (q0, and every water body the mirror pass isn't on): pale luminous haze at the
  // horizon deepening to gold overhead, a warm glow toward the sun; it dims with the night
  float ry = abs( R.y );
  vec3 skyTop = mix( uSkyTop, uHazeColor * 0.8, uNight );
  vec3 env = mix( mix( uHazeColor, uSkyHorizon, 0.35 * ( 1.0 - uNight ) ), skyTop, smoothstep( 0.03, 0.7, ry ) );
  env = mix( env, uSunHazeColor * 1.08, pow( sunR, mix( 6.0, 14.0, still ) ) * 0.7 );
  // still water takes the same warm print grade as the mirror, so a pool off the mirror's plane
  // still comes back gold, not a pale grey sheet
  env *= mix( vec3( 1.0 ), vec3( 1.06, 0.97, 0.8 ), still );
  vec3 sky = env;
  float pw = 0.0, objK = 0.0;
  #if WQ >= 1
  // the planar mirror (reflection.ts): only on still water at the mirrored plane's height
  pw = uReflOn * still * ( 1.0 - smoothstep( 0.15, 0.6, abs( vHazeWorld.y - uReflY ) ) );
  if ( pw > 0.002 ) {
    vec4 rc = uReflMat * vec4( vHazeWorld, 1.0 );
    // gentle ripples break the mirror: a screen-space wobble, longer vertically (reflections
    // smear down the water toward the viewer), shrinking with distance
    float wk = 0.075 / ( 4.0 + dist * 0.3 );
    vec2 wob = vec2( n1.r + n2.g - 1.0, ( n1.g + n2.r - 1.0 ) * 1.7 ) * wk;
    // and long soft ripple lines lying across the view, sliding slowly
    wob.x += sin( dot( vHazeWorld.xz, vec2( 0.31, 1.7 ) ) + ( n1.r - 0.5 ) * 6.0 - t * 0.9 ) * wk * 0.5;
    vec2 ruv = rc.xy / rc.w + wob;
    vec3 rcol = texture2D( uRefl, ruv ).rgb;
    #if WQ >= 2
    // painterly softening: a small vertical-leaning blur instead of a crisp chrome image
    vec2 o1 = vec2( uReflPx.x * 1.3, uReflPx.y * 2.6 ), o2 = vec2( -uReflPx.x * 1.3, uReflPx.y * 2.6 );
    rcol = rcol * 0.36 + ( texture2D( uRefl, ruv + o1 ).rgb + texture2D( uRefl, ruv - o1 ).rgb
      + texture2D( uRefl, ruv + o2 ).rgb + texture2D( uRefl, ruv - o2 ).rgb ) * 0.16;
    #endif
    // the image is only valid inside the mirror camera's frame
    pw *= smoothstep( 0.0, 0.04, ruv.x ) * smoothstep( 1.0, 0.96, ruv.x ) * smoothstep( 0.0, 0.04, ruv.y ) * smoothstep( 1.0, 0.96, ruv.y );
    // the mirrored world has already been hazed along its whole path, and the water hazes it
    // again: push it back off the haze colour a little so the reflection keeps its shapes
    rcol = max( rcol + ( rcol - uHazeColor ) * 0.1, vec3( 0.0 ) );
    // warmed like an old print: the blue-grey far ranges come back olive-slate, never as a blue
    // sky, and the pale haze comes back gold
    rcol = min( rcol, vec3( 1.25 ) ) * vec3( 1.08, 0.98, 0.8 );
    // and lightly banded in value, like the printed sky and grass
    float rl = dot( rcol, vec3( 0.3, 0.59, 0.11 ) );
    rcol *= mix( 1.0, ( floor( rl * 6.0 ) + 0.5 ) / 6.0 / max( rl, 1e-3 ), 0.3 );
    rcol = max( mix( vec3( rl ), rcol, 1.15 ), vec3( 0.0 ) );
    // painted, not physical: things standing over the water (spires, rims, trees, the town) come
    // back strong and dark even where the sky's reflection is faint, so they read as mirrored
    objK = smoothstep( 0.14, 0.42, dot( env - rcol, vec3( 0.3, 0.59, 0.11 ) ) ) * pw;
    env = mix( env, rcol, pw );
  }
  #endif
  // body: deep teal in the middle, turquoise over the shallow margins
  float shallow = ( 1.0 - smoothstep( 0.0, min( bankR * 0.5, 4.5 ), dB ) ) * ( 1.0 - farW * 0.5 );
  vec3 body = mix( uDeep, uShallow, shallow );
  // looking down into it, light pools on the bed (low-frequency caustic-ish breakup)
  // (not on still water: the print pass posterizes, and slow noise there flips whole patches of
  // the mirror between two bands)
  body *= 0.92 + 0.16 * mix( texture2D( uNoise, sc * 2.3 + n1.rg * 0.05 ).a, 0.5, still );
  // with distance the water turns to a luminous turquoise so it still reads through the gold
  // haze (a dark teal just greys out against it): the bright ribbon you spot from the air
  // (still water only keeps it at a grazing glance: looking down from the air it stays deep)
  body = mix( body, uFar * mix( 0.72, 1.0, smoothstep( 0.25, 0.9, edge ) ), smoothstep( 20.0, 110.0, dist ) * ( 1.0 - still * smoothstep( 0.18, 0.45, -V.y ) ) );
  // moving water: the sky comes back mostly on the far, grazing water as broken gold; up close
  // the teal body rules
  float refl = smoothstep( 0.3, 0.85, fres + ( n2.b - 0.5 ) * 0.2 );
  vec3 col = mix( body, sky, refl * 0.55 * ( 1.0 - farW * 0.4 ) );
  // still water: a calm painted mirror. Looking down it's a deep, dark teal; toward grazing the
  // world comes back (the sky, the sun's glow, the spires and rims and trees), slightly cooled
  // and darkened by the water, softly broken by the ripples
  // (the fresnel takes a calmed normal: at grazing angles the full ripple normal swings it
  // between patches of mirror and patches of body, which reads as blotches, not ripples)
  float cosC = clamp( -dot( V, normalize( vec3( N.x * 0.25, N.y, N.z * 0.25 ) ) ), 0.0, 1.0 );
  float fresS = 0.1 + 0.9 * pow( 1.0 - cosC, 2.6 );
  // (the deepening fades out with distance, where the body turns luminous so it reads from the air)
  // looking down (from the air too) the middle goes deep; at a far grazing glance the luminous
  // far turquoise stays, so the lake still reads through the haze
  float downK = smoothstep( 0.15, 0.5, cosC );
  // still water's body is the palette's muted green-grey teal, never a swimming-pool cyan
  // (its shallow margin is a dimmer olive-teal, not the running water's bright turquoise, which
  // under a gold reflection mixes to a milky blue-grey)
  vec3 bodyS = mix( body, mix( uDeep, uShallow * vec3( 0.62, 0.6, 0.45 ), shallow ), 0.6 * ( 1.0 - farW ) );
  bodyS = mix( bodyS, vec3( dot( bodyS, vec3( 0.3, 0.59, 0.11 ) ) ) * vec3( 0.72, 1.05, 0.98 ), 0.35 * ( 1.0 - farW ) );
  // the shallow rim goes amber-olive (the bank's sediment seen through it), never a bright cyan line
  bodyS = mix( bodyS, vec3( 0.21, 0.2, 0.09 ), smoothstep( 1.4, 0.1, dB ) * 0.55 * ( 1.0 - farW ) );
  vec3 deepS = mix( bodyS, uDeep * 0.62, ( 0.68 - 0.3 * shallow ) * max( downK, 0.35 ) );
  // a quieter, greyer teal than running water: teal is the shadow note of this palette
  deepS = mix( deepS, vec3( dot( deepS, vec3( 0.3, 0.59, 0.11 ) ) ) * vec3( 0.9, 1.06, 0.92 ), 0.12 );
  // the world comes back warm (gold sky, amber rims), only a touch darker than it is
  float wR = fresS * mix( 0.7, 0.97, pw ) * mix( 1.0, 0.45, downK );
  wR = wR + ( 1.0 - wR ) * objK * 0.6;
  vec3 envW = env * mix( 1.0, 0.72, downK * ( 1.0 - objK ) ) * mix( vec3( 1.0 ), vec3( 0.86, 0.98, 1.04 ), still * ( 1.0 - farW ) );
  vec3 stillCol = mix( deepS, envW, wR );
  // teal under cream mixes to grey: lift the saturation back so the blend stays a colour (a
  // painter's glaze, not a photographic average)
  stillCol = max( mix( vec3( dot( stillCol, vec3( 0.3, 0.59, 0.11 ) ) ), stillCol, 1.0 + 1.3 * wR * ( 1.0 - wR ) ), vec3( 0.0 ) );
  // the sun's own path: a soft warm column of light down the water toward the viewer
  // (a broad glow at a grazing glance, only a tight hot core seen from above)
  // (the hot core only up close: from afar the rippled normals turn it into a glitter web)
  float sunCol = pow( sunR, 12.0 ) * 0.12 * ( 1.0 - downK ) + pow( sunR, 90.0 ) * 0.45 * ( 1.0 - 0.75 * downK )
    + pow( sunR, 700.0 ) * 0.9 * ( 1.0 - smoothstep( 18.0, 40.0, dist ) );
  stillCol += uSunHazeColor * sunCol * ( 0.45 + 0.55 * smoothstep( 0.35, 0.8, texture2D( uNoise, a1 * 0.45 + 0.17 ).a ) ) * ( 1.0 - uNight );
  col = mix( col, stillCol, still );
  // light catching the ripple crests: warped pale dashes lying across the current
  #if WQ >= 1
  vec2 rq = kind > 0.5 ? vec2( vUV.x, vUV.y - t * spd * 0.8 ) : vUV + t * vec2( 0.05, 0.12 );
  float wv = sin( ( kind > 0.5 ? rq.y : rq.y + rq.x * 0.4 ) * 2.3 + ( n1.r - 0.5 ) * 9.0 + ( kind > 0.5 ? rq.x * 0.25 : 0.0 ) );
  float dash = smoothstep( 0.86, 0.95, wv ) * smoothstep( 0.45, 0.65, n2.g ) * ( 1.0 - farW * 0.7 );
  col = mix( col, mix( uShallow, uFoam, 0.55 ), dash * 0.55 * ( 1.0 - still ) );
  // on still water the same crests are thin darker ripple strokes across the mirror (a painter's
  // broken line through the reflection), only at a low glance, gone from the air
  col = mix( col, deepS * 0.9, dash * 0.3 * still * wR * ( 1.0 - downK ) * ( 1.0 - farW ) );
  #endif
  // flow streaks: long pale lines combed along the current
  float st = texture2D( uNoise, sc ).b;
  float streak = smoothstep( 0.66, 0.8, st ) * ( kind > 0.5 ? clamp( 0.35 + spd * 0.25, 0.0, 0.85 ) : 0.25 );
  col = mix( col, mix( uShallow * 1.35, uFoam, 0.5 ), streak * 0.55 * ( 1.0 - farW * 0.7 ) * ( 1.0 - still ) );
  // foam: a broken cream line hugging every bank, and white water on the rapids
  float fn = texture2D( uNoise, ( kind > 0.5 ? vec2( vUV.x * 0.5, ( vUV.y - t * spd ) * 0.16 ) : vUV * 0.28 + t * 0.01 ) ).b;
  float bank = smoothstep( 0.42, 0.15, dB + ( fn - 0.5 ) * 1.2 * min( bankR / 3.0, 1.0 ) ) * ( 1.0 - smoothstep( 1.0, 1.1, edge ) ) * 0.85;
  float rap = smoothstep( 0.55, 0.4, fn - froth * 0.45 ) * smoothstep( 0.05, 0.35, froth ) * 0.9;
  float foam = max( bank * mix( 0.9, 0.16, still ) * ( vD.z > 0.0 ? 0.25 : 1.0 ), rap ) * ( 1.0 - farW * 0.65 );
  col = mix( col, uFoam, foam );
  // sun glints: hard printed sparkles in the sun's mirror path
  // (on still water they're fewer and finer: the mirror already carries the sun's glow, and a
  // broad glitter sheet would wash the reflections out)
  float gl = pow( sunR, mix( 90.0, 360.0, still ) ) * smoothstep( mix( 0.55, 0.78, still ), mix( 0.75, 0.86, still ), n2.a + n1.b * 0.4 );
  // (still water seen from afar keeps only the soft sun column: a field of specks reads as a tiled texture)
  col += uSunHazeColor * gl * ( 1.6 - foam ) * mix( 1.2, 0.35, still ) * ( 1.0 - still * smoothstep( 14.0, 36.0, dist ) );
  // wet dark line where the water meets the bank: a printed ink edge that reads from the air
  float wet = smoothstep( 0.18, 0.0, dB ) * ( 1.0 - smoothstep( 1.05, 1.11, edge ) );
  col = mix( col, uDeep * 0.6, wet * 0.5 );
  // slight soft fade-in at the very outer rim where the ribbon tucks under the bank
  float alpha = mix( 0.94, 0.8, shallow );
  // still water clears over its shallow rim, so the real bank shows through as sediment
  alpha = mix( alpha, 0.74, still * smoothstep( kind > 0.5 ? 0.6 : 1.3, 0.12, dB ) * ( 1.0 - farW ) );
  alpha = max( max( alpha, foam ), wet * 0.6 );
  alpha *= 1.0 - smoothstep( 1.06, 1.12, edge );
  if ( wd < 8.0 ) {
    // warm translucent shallows: the sediment shows through where the water thins
    float shl = ( 1.0 - smoothstep( 0.03, 0.22, wd ) ) * ( 1.0 - farW ) * ( 1.0 - wR );
    col = mix( col, vec3( 0.3, 0.26, 0.12 ), shl * 0.3 );
    alpha = mix( alpha, 0.62, shl * 0.5 );
    // the bank: a dark wet band just inside the waterline, a faint broken lap line on it, and
    // the water fading out where it thins to nothing (no hard polygon edge over the grass)
    float band = smoothstep( 0.0, 0.06, wd ) * ( 1.0 - smoothstep( 0.16, 0.45, wd ) ) * ( 1.0 - farW );
    col = mix( col, uDeep * 0.42 + vec3( 0.03, 0.025, 0.0 ), band * 0.42 );
    float lap = ( 1.0 - smoothstep( 0.0, 0.06, abs( wd - 0.03 ) ) ) * smoothstep( 0.45, 0.7, fn ) * ( 1.0 - farW );
    col = mix( col, uFoam * 0.9, lap * 0.35 );
    // where a rock stands in the water: a dark contact ring hugging its foot and a lap line on it
    float ring = smoothstep( 0.55, 0.85, rockK + ( fn - 0.5 ) * 0.12 ) * ( 1.0 - farW );
    col = mix( col, uDeep * 0.38 + vec3( 0.025, 0.02, 0.0 ), ring * 0.62 );
    col = mix( col, uFoam * 0.9, smoothstep( 0.93, 0.99, rockK + ( fn - 0.5 ) * 0.08 ) * 0.22 * ( 1.0 - farW ) );
    alpha = max( alpha, max( band, ring ) * 0.85 );
    alpha *= smoothstep( -0.04, 0.05, wd );
  }
  gl_FragColor = vec4( col, alpha );
  #include <colorspace_fragment>
  #include <fog_fragment>
  // still water seen from above keeps some of its depth through the haze (a darker glaze over
  // the hazed colour), so a pond reads as deep water from the air, not as a pale hole
  gl_FragColor.rgb *= mix( vec3( 1.0 ), vec3( 0.52, 0.66, 0.66 ), still * smoothstep( 0.15, 0.45, -V.y ) * smoothstep( 22.0, 60.0, dist ) * ( 1.0 - foam ) );
}
`;

const FALL_VERT = /* glsl */ `
#include <common>
attribute vec2 aUV;
attribute vec4 aF;
uniform float uTime;
varying vec2 vUV;
varying vec4 vF;
varying vec3 vN;
void main() {
  vUV = aUV; vF = aF;
  vec3 transformed = position;
  // the sheet breathes: a slow lateral ripple that grows down the fall
  float w = sin( uTime * 1.7 + aUV.y * 0.35 + aF.w * 3.0 ) * 0.12 + sin( uTime * 2.9 - aUV.y * 0.8 + aUV.x * 2.0 ) * 0.06;
  transformed += normal * w * smoothstep( 0.0, 6.0, aUV.y );
  vN = normal;
  #include <project_vertex>
}
`;

const FALL_FRAG = /* glsl */ `
#include <common>
${COMMON_FRAG}
varying vec2 vUV;
varying vec4 vF;
varying vec3 vN;
void main() {
  float u = vUV.x, d = vUV.y;           // across (-1..1), metres fallen
  float vn = vF.x, haze = vF.y, hw = vF.z, seed = vF.w;
  float t = uTime;
  // streaks stretch as the water accelerates: index them by sqrt of the drop
  float sd = sqrt( max( d, 0.0 ) );
  // vertical foam ropes: warped stripes across the sheet, broken by slow noise sliding down
  vec2 c1 = vec2( u * hw * 0.14 + seed, sd * 0.05 - t * 0.35 );
  vec2 c2 = vec2( u * hw * 0.3 + seed * 1.7, sd * 0.16 - t * 0.9 );
  float s1 = texture2D( uNoise, c1 ).b, s2 = texture2D( uNoise, c2 ).g;
  // ropes split as the sheet fans out (more of them, thinner, lower down an edge fall)
  float ropes = 0.5 + 0.5 * sin( u * hw * 4.2 * ( 1.0 + haze * vn * 1.6 ) + s1 * 7.0 + seed * 5.0 );
  float streak = smoothstep( 0.55, 0.85, ropes * 0.65 + s2 * 0.55 );
  vec3 toFrag = vHazeWorld - cameraPosition;
  vec3 V = normalize( toFrag );
  // body: glassy teal over the lip, quickly whitening into falling foam
  float white = smoothstep( 0.3, 2.2, d );
  vec3 glass = mix( uShallow, uDeep, 0.25 );
  // falling water: cream foam ropes with teal glass showing between them
  vec3 col = mix( glass, mix( mix( uShallow, uDeep, 0.2 ), uFoam, streak ), white );
  // a bright rolled highlight right on the lip
  col = mix( col, uFoam * 1.05, smoothstep( 0.0, 0.25, d ) * ( 1.0 - smoothstep( 0.3, 0.8, d ) ) * 0.8 );
  // sun: lit cream on the sunward face, glowing warm when back-lit (looking toward the sun)
  float lit = clamp( dot( normalize( vN ), uSunDir ) * 0.5 + 0.5, 0.0, 1.0 );
  float back = pow( max( dot( V, uSunDir ), 0.0 ), 4.0 );
  col *= 0.78 + 0.32 * lit;
  col = mix( col, uSunHazeColor * 1.1, back * 0.2 * streak );
  // churn: thick white where it tips over and where it lands
  float lipC = smoothstep( 0.0, 1.0, d ) * ( 1.0 - smoothstep( 1.5, 4.5, d ) );
  float land = ( 1.0 - haze ) * smoothstep( 0.78, 1.0, vn );
  col = mix( col, uFoam, max( lipC * 0.55, land * 0.8 ) * ( 0.6 + 0.4 * s2 ) );
  // edges: ragged, thinning to spray
  float eN = texture2D( uNoise, vec2( sd * 0.17 - t * 0.5, u * 0.5 + seed ) ).r;
  float side = 1.0 - smoothstep( 0.55, 1.0, abs( u ) + ( eN - 0.5 ) * ( 0.25 + 0.5 * white ) );
  float alpha = mix( 0.88, 0.72 + 0.27 * streak, white ) * side;
  alpha = max( alpha, land * 0.85 * side );
  // edge falls come apart into mist and haze toward the bottom
  // edge falls: past the first stretch the sheet breaks into separate streaks of spray, which
  // thin out and dissolve into the haze over the last third (50-80 m down)
  float brk = smoothstep( 0.12, 0.55, vn + ( s1 - 0.5 ) * 0.2 );
  float sprayA = smoothstep( 0.25, 0.7, streak + ( s2 - 0.5 ) * 0.4 ) * 0.85 + 0.2;
  float sideR = 1.0 - smoothstep( 0.35, 1.0, abs( u ) + ( eN - 0.5 ) * 0.9 );
  float tail = 1.0 - smoothstep( 0.62, 1.0, vn + ( s2 - 0.5 ) * 0.3 );
  alpha = mix( alpha, sprayA * sideR * tail, haze * brk );
  // the foam apron (vn 1 -> 1.5) melts into the pool in front of the fall
  alpha *= 1.0 - smoothstep( 1.05, 1.45, vn );
  col = mix( col, mix( uFoam, uSunHazeColor, 0.3 * vn ), haze * brk * 0.6 );
  if ( alpha < 0.01 ) discard;
  gl_FragColor = vec4( col, alpha );
  #include <colorspace_fragment>
  #include <fog_fragment>
}
`;

const MIST_VERT = /* glsl */ `
#include <common>
attribute vec2 aCorner;
attribute vec4 aM;   // size, phase, fall (m the puff sinks over its life), alpha
attribute vec3 aDir; // drift direction (out from the cliff)
uniform float uTime;
varying vec2 vC;
varying float vA;
varying float vSeed;
void main() {
  float life = fract( uTime * ( 0.16 + fract( aM.y * 7.3 ) * 0.12 ) + aM.y );
  vec3 c = position;
  c += aDir * life * ( 1.0 + aM.x * 0.25 );
  c.y += aM.z < 0.0 ? life * aM.x * 0.45 : -life * aM.z;
  float size = aM.x * ( 0.55 + 0.65 * life );
  vec3 right = vec3( viewMatrix[ 0 ][ 0 ], viewMatrix[ 1 ][ 0 ], viewMatrix[ 2 ][ 0 ] );
  vec3 up = vec3( viewMatrix[ 0 ][ 1 ], viewMatrix[ 1 ][ 1 ], viewMatrix[ 2 ][ 1 ] );
  float rot = aM.y * 6.28 + uTime * 0.15 * ( fract( aM.y * 3.1 ) - 0.5 );
  vec2 k = vec2( cos( rot ) * aCorner.x - sin( rot ) * aCorner.y, sin( rot ) * aCorner.x + cos( rot ) * aCorner.y );
  // pulled toward the camera by most of its size, so a puff never slices into the bank (no depth
  // texture for soft particles: this keeps the ground from cutting a hard line through it)
  vec3 toCam = cameraPosition - c;
  float camD = length( toCam );
  vec3 transformed = c + ( right * k.x + up * k.y ) * size + toCam / max( camD, 1e-3 ) * min( size * 0.8, camD * 0.5 );
  vC = aCorner;
  vSeed = aM.y;
  vA = aM.w * smoothstep( 0.0, 0.18, life ) * ( 1.0 - smoothstep( 0.55, 1.0, life ) );
  #include <project_vertex>
}
`;

const MIST_FRAG = /* glsl */ `
#include <common>
${COMMON_FRAG}
varying vec2 vC;
varying float vA;
varying float vSeed;
void main() {
  float r = length( vC );
  float n = texture2D( uNoise, vC * 0.28 + vSeed * 3.7 + uTime * 0.01 ).g;
  // the noise lumps the edge, but never past the unit circle (or the quad's corners show)
  float a = smoothstep( 1.0, 0.25, r + ( n - 0.5 ) * 0.7 ) * ( 1.0 - smoothstep( 0.75, 1.0, r ) ) * vA;
  if ( a < 0.01 ) discard;
  // cream mist, warm where the sun catches its top
  vec3 col = mix( uFoam * 0.9, uSunHazeColor * 1.05, clamp( vC.y * 0.5 + 0.5, 0.0, 1.0 ) * 0.5 );
  gl_FragColor = vec4( col, a );
  #include <colorspace_fragment>
  #include <fog_fragment>
}
`;

// ------------------------------------------------------------------ builders
interface Buf { pos: number[]; uv: number[]; p: number[]; d: number[]; idx: number[] }

const len2 = (x: number, z: number) => Math.sqrt(x * x + z * z);

export class WaterView {
  readonly group = new THREE.Group();
  private q: number;
  private disposables: { dispose(): void }[] = [];
  /** the planar mirror for the still water nearest on screen (q1+; q0 uses the analytic sky) */
  private refl: PlanarReflection | null = null;
  private falls: THREE.Mesh | null = null;

  constructor(world: World, quality: number) {
    const q = Math.max(0, Math.min(3, quality | 0));
    this.q = q;
    this.group.name = 'water';
    const noise = makeNoise(q >= 2 ? 128 : 64);
    this.disposables.push(noise);

    const shared = {
      uNoise: { value: noise },
      uDeep: { value: new THREE.Color(0x0a5c62) },
      uShallow: { value: new THREE.Color(0x3fb8a4) },
      uFoam: { value: new THREE.Color(0xf7efd6) },
      uFar: { value: new THREE.Color(0x3fc4b8) },
      uSkyTop: { value: new THREE.Color(0xf3b467) },
      uSkyHorizon: { value: new THREE.Color(0xffe3ae) },
      uQ: { value: q },
    };

    // ---------------------------------------------------------------- 1. surfaces
    const S: Buf = { pos: [], uv: [], p: [], d: [], idx: [] };
    // the ponds' depth map (see bakePondDepth): one atlas tile per pond
    const depth = bakePondDepth(world);
    this.disposables.push(depth.tex);
    const ponds = world.ponds;
    const sinkInPond = (x: number, z: number, y: number) => {
      // where a stream runs level through a pool, the pool's surface wins (no z-fighting)
      for (const p of ponds) {
        const dp = len2(x - p.x, z - p.z);
        if (dp < p.r + 0.9 && y < p.y + 0.25) y = Math.min(y, p.y - 0.09);
        // a stream that runs into a pond a little above its level (the sim's ribbon can sit up to
        // ~0.7 m over the pool inside its rim) tucks under the pond too, instead of floating
        // across it as a tilted slab
        else if (dp < p.r * 0.92 && y < p.y + 1.5) y = p.y - 0.09;
      }
      return y;
    };
    const nearStream = (x: number, z: number) => {
      let best = 1e9;
      for (const s of world.streams) for (const g of s.segs) {
        if (x < g.x0 || x > g.x1 || z < g.z0 || z > g.z1) continue;
        const rx = x - g.ax, rz = z - g.az;
        const tt = Math.max(0, Math.min(g.len, rx * g.ux + rz * g.uz));
        const d = len2(rx - g.ux * tt, rz - g.uz * tt) - g.hw;
        if (d < best) best = d;
      }
      return best;
    };

    // ponds: polar grids, rings packed toward the bank where the shading changes
    const ringF = q >= 2 ? [0, 0.3, 0.55, 0.72, 0.84, 0.92, 0.97, 1.0, 1.06] : [0, 0.5, 0.8, 0.95, 1.0, 1.06];
    ponds.forEach((p, pi) => {
      const tile = depth.tiles[pi];
      const segN = Math.max(20, Math.min(q >= 2 ? 112 : 56, Math.round(p.r * (q >= 2 ? 4.2 : 2.4))));
      const base = S.pos.length / 3;
      // centre
      S.pos.push(p.x, p.y, p.z); S.uv.push(p.x, p.z); S.p.push(0, p.r, 0, 0); S.d.push(...tile);
      for (let ri = 1; ri < ringF.length; ri++) {
        const rr = ringF[ri] === 1.06 ? p.r + 0.9 : p.r * ringF[ri];
        for (let i = 0; i < segN; i++) {
          const a = (i / segN) * Math.PI * 2;
          const x = p.x + Math.cos(a) * rr, z = p.z + Math.sin(a) * rr;
          let e = rr / p.r;
          // no bank foam across a stream mouth: the stream ribbon carries its own
          if (e > 0.6) { const ns = nearStream(x, z); if (ns < 1.5) e = Math.min(e, 0.6 + 0.4 * Math.max(0, ns + 1) / 2.5); }
          S.pos.push(x, p.y, z); S.uv.push(x, z); S.p.push(e, p.r, 0, 0); S.d.push(...tile);
        }
      }
      for (let i = 0; i < segN; i++) S.idx.push(base, base + 1 + ((i + 1) % segN), base + 1 + i);
      for (let ri = 1; ri < ringF.length - 1; ri++) {
        const r0 = base + 1 + (ri - 1) * segN, r1 = r0 + segN;
        for (let i = 0; i < segN; i++) {
          const i1 = (i + 1) % segN;
          S.idx.push(r0 + i, r0 + i1, r1 + i, r0 + i1, r1 + i1, r1 + i);
        }
      }
    });

    // streams: one continuous ribbon per chained run of segments
    const across = q === 0 ? [-1.12, -0.6, 0, 0.6, 1.12] : [-1.12, -0.96, -0.8, -0.5, 0, 0.5, 0.8, 0.96, 1.12];
    for (const s of world.streams) {
      let run: StreamSeg[] = [];
      const flush = () => {
        if (run.length) this.ribbon(S, run, across, sinkInPond);
        run = [];
      };
      for (const g of s.segs) {
        const prev = run[run.length - 1];
        if (prev && (Math.abs(prev.bx - g.ax) > 0.05 || Math.abs(prev.bz - g.az) > 0.05)) flush();
        run.push(g);
      }
      flush();
    }
    const surfGeo = new THREE.BufferGeometry();
    surfGeo.setAttribute('position', new THREE.Float32BufferAttribute(S.pos, 3));
    surfGeo.setAttribute('aUV', new THREE.Float32BufferAttribute(S.uv, 2));
    surfGeo.setAttribute('aP', new THREE.Float32BufferAttribute(S.p, 4));
    surfGeo.setAttribute('aD', new THREE.Float32BufferAttribute(S.d, 4));
    surfGeo.setIndex(S.idx);
    surfGeo.computeBoundingSphere();
    // haze capped a little under the island tops (0.45) so the water still reads from the air
    const surfMat = this.shader(SURF_VERT, SURF_FRAG, shared, 'waterSurf', { transparent: true, depthWrite: true }, 0.4);
    const surf = new THREE.Mesh(surfGeo, surfMat);
    surfMat.uniforms.uDepth = { value: depth.tex };
    if (q >= 1 && S.idx.length) {
      const refl = new PlanarReflection(world, q);
      this.refl = refl;
      Object.assign(surfMat.uniforms, refl.uniforms);
      this.disposables.push(refl);
      // the mirror renders just before the water is drawn (with the final camera), only when the
      // water is in view at all; reflection.ts skips the pass when no still water is on screen
      surf.onBeforeRender = (renderer, scene, camera) => refl.render(renderer, scene, camera, HAZE.uTime.value as number);
    }
    surf.name = 'waterSurface';
    surf.renderOrder = 1;
    this.group.add(surf);
    this.disposables.push(surfGeo, surfMat);

    // ---------------------------------------------------------------- 2. falls + 3. mist
    const F = { pos: [] as number[], nrm: [] as number[], uv: [] as number[], f: [] as number[], idx: [] as number[] };
    const M = { pos: [] as number[], corner: [] as number[], m: [] as number[], dir: [] as number[], idx: [] as number[] };
    let rs = 9157;
    const rnd = () => { rs = (rs * 16807) % 2147483647; return rs / 2147483647; };
    const puff = (x: number, y: number, z: number, size: number, sink: number, alpha: number, dx: number, dy: number, dz: number) => {
      const b = M.pos.length / 3, ph = rnd();
      for (const [cx, cy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
        M.pos.push(x, y, z); M.corner.push(cx, cy); M.m.push(size, ph, sink, alpha); M.dir.push(dx, dy, dz);
      }
      M.idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
    };
    const mistMul = [0.4, 0.7, 1, 1.4][q];

    world.falls.forEach((f, fi) => {
      const H = Math.max(0.5, f.y1 - f.y0);
      const haze = f.edge && H > 50 ? 1 : 0;
      const px = -f.uz, pz = f.ux; // across
      // where it lands, horizontally: cascades follow the cliff, plate falls arc to the plate
      let reach = 4.2;
      // a cascade inside the island: run the sheet down the whole cliff the stream drops over,
      // to where the stream levels out again at the foot (its tilted ribbon is skipped there)
      const foot = f.edge ? -1 : this.cascadeFoot(world, f.x, f.z, f.y0);
      if (foot > 0) reach = foot + 1.6;
      else if (!haze) {
        reach = 0;
        for (let k = 0.5; k <= 14; k += 0.5) {
          const g = world.terrain(f.x + f.ux * k, f.z + f.uz * k);
          reach = k;
          if (g !== NO_GROUND && g <= f.y0 + 0.4) break;
        }
        reach = Math.max(1.2, Math.min(f.edge ? 6 : 5, reach));
      }
      const rows = Math.max(6, Math.round((haze ? 28 : 6 + H * 0.6) * (q >= 2 ? 1 : 0.6)));
      // edge falls trail on past the sim's 70 m into the haze below the island
      const L = haze ? H + 12 : H;
      const colsF = q >= 2 ? 7 : 5;
      const back = 1.4; // the sheet starts behind the lip, lying on the stream, so the two meet
      const base = F.pos.length / 3;
      // falls that land on island get a foam apron: the sheet's foot turns out flat across the
      // pool below, so the seam with the stream ribbon hides under white water
      const apron = haze ? 0 : 1;
      const nRows = rows + 2 + apron;
      for (let j = 0; j < nRows; j++) {
        // j 0,1 = flat lead-in over the stream; j>=1 = falling (row 1 is the lip itself)
        let along: number, y: number, d: number, vn: number;
        if (j === 0) { along = -back; y = f.y1 + 0.03; d = -back; vn = 0; }
        else if (j === rows + 2) { along = reach + 2.2; y = f.y0 + 0.06; d = H + 2; vn = 1.5; }
        else {
          const tt = (j - 1) / rows;
          const tq = Math.pow(tt, 1.5);
          d = L * tq;
          // edge falls: the spray blows out from the cliff as it falls
          along = reach * Math.sqrt(tq) + 0.2 * tt + (haze ? 6 * tq : 0);
          y = f.y1 + 0.03 - d;
          vn = tt;
        }
        // width: cascades stay inside their gorge; edge falls keep a crisp lip at the sim width,
        // open to ~1.8x within the first third and flare to ~3x as they come apart below
        const wm = j === rows + 2 ? 0.85 : !haze ? 0.97
          : 1 + 0.8 * Math.min(1, Math.max(0, vn) / 0.3) + 1.3 * Math.max(0, vn - 0.3) / 0.7;
        // tangent along the arc (for the normal): d(along)/d(y)
        const tq = Math.max(1e-3, j === 0 ? 0 : Math.pow(Math.min(1, (j - 1) / rows), 1.5));
        const dAl = j <= 1 ? 1 : (reach * 0.5 / Math.sqrt(tq) + (haze ? 6 : 0)) / L; // horizontal per metre of drop
        let tx = f.ux * dAl, ty = j <= 1 ? 0 : -1, tz = f.uz * dAl;
        if (j <= 1) { tx = f.ux; ty = -0.3; tz = f.uz; }
        const tl = Math.hypot(tx, ty, tz); tx /= tl; ty /= tl; tz /= tl;
        // normal = across x tangent (points outward from the cliff)
        let nx = -pz * ty, ny = pz * tx - px * tz, nz = px * ty;
        const nl = Math.hypot(nx, ny, nz) || 1; nx /= nl; ny /= nl; nz /= nl;
        if (nx * f.ux + nz * f.uz < 0) { nx = -nx; ny = -ny; nz = -nz; }
        for (let i = 0; i < colsF; i++) {
          const u = (i / (colsF - 1)) * 2 - 1;
          const w = u * f.hw * wm;
          // bowed: the middle of the sheet throws a little further than its edges
          const bow = (1 - u * u) * 0.35 * Math.min(1, Math.max(0, d) / 3);
          const x = f.x + f.ux * (along + bow) + px * w;
          const z = f.z + f.uz * (along + bow) + pz * w;
          let yy = y;
          if (!haze && j > 1 && j <= rows + 1) {
            // cascades never cut into their cliff: lift the whole row off the gorge floor under
            // its centre line (lifting each column to its own ground laid the sides flat on the
            // banks as a pale slab)
            const cx = f.x + f.ux * along, cz = f.z + f.uz * along;
            const g = world.terrain(cx, cz);
            if (g !== NO_GROUND && yy < g + 0.25 && d < H - 0.3) yy = Math.min(f.y1, g + 0.25);
          }
          F.pos.push(x, yy, z); F.nrm.push(nx, ny, nz);
          F.uv.push(u, Math.max(0, d)); F.f.push(Math.max(0, vn), haze, f.hw, fi * 0.37 + 0.11);
        }
      }
      for (let j = 0; j < nRows - 1; j++) for (let i = 0; i < colsF - 1; i++) {
        const a = base + j * colsF + i, b = a + 1, c = a + colsF, e = c + 1;
        F.idx.push(a, c, b, b, c, e);
      }

      // mist: lip churn, landing churn, and for the long edge falls a column dissolving into haze
      const lipN = Math.max(1, Math.round(3 * mistMul));
      for (let k = 0; k < lipN; k++) {
        const u = (k + 0.5) / lipN * 2 - 1;
        puff(f.x + f.ux * 1.2 + px * u * f.hw * 0.8, f.y1 - 1.2, f.z + f.uz * 1.2 + pz * u * f.hw * 0.8,
          1.0 + f.hw * 0.2, 1.5, 0.3, f.ux * 0.4, 0, f.uz * 0.4);
      }
      if (haze) {
        // a thin veil of spray down the whole length (no ball at the end: the sheet itself
        // trails off into the haze)
        const n = Math.round(12 * mistMul);
        for (let k = 0; k < n; k++) {
          const tt = 0.2 + 0.75 * (k / Math.max(1, n - 1));
          const tq = Math.pow(tt, 1.5);
          const d = L * tq;
          const al = reach * Math.sqrt(tq) + 6 * tq + 1;
          const wide = 1 + 0.8 * Math.min(1, tt / 0.3) + 1.3 * Math.max(0, tt - 0.3) / 0.7;
          const u = (rnd() * 2 - 1) * 0.9;
          puff(f.x + f.ux * al + px * u * f.hw * wide, f.y1 - d, f.z + f.uz * al + pz * u * f.hw * wide,
            2.5 + tt * 6 + f.hw * 0.5, 5 + tt * 6, 0.38 * (1 - tt * 0.6), f.ux * 1.2, 0, f.uz * 1.2);
        }
      } else {
        const n = Math.max(2, Math.round((4 + Math.min(H, 24) * 0.25) * mistMul));
        for (let k = 0; k < n; k++) {
          const u = (rnd() * 2 - 1) * 0.9;
          const al = reach + 0.6 + rnd() * 1.6;
          const sz = 1.2 + f.hw * 0.3 + Math.min(H, 24) * 0.07;
          puff(f.x + f.ux * al + px * u * f.hw * 0.7, f.y0 + 0.4 + sz * 0.35, f.z + f.uz * al + pz * u * f.hw * 0.7,
            sz, -1, 0.32, f.ux * 0.8, 0, f.uz * 0.8);
        }
      }
    });

    if (F.idx.length) {
      const fallGeo = new THREE.BufferGeometry();
      fallGeo.setAttribute('position', new THREE.Float32BufferAttribute(F.pos, 3));
      fallGeo.setAttribute('normal', new THREE.Float32BufferAttribute(F.nrm, 3));
      fallGeo.setAttribute('aUV', new THREE.Float32BufferAttribute(F.uv, 2));
      fallGeo.setAttribute('aF', new THREE.Float32BufferAttribute(F.f, 4));
      fallGeo.setIndex(F.idx);
      fallGeo.computeBoundingSphere();
      const fallMat = this.shader(FALL_VERT, FALL_FRAG, shared, 'waterFall', { transparent: true, depthWrite: false, side: THREE.DoubleSide }, 0.6);
      const falls = new THREE.Mesh(fallGeo, fallMat);
      falls.name = 'waterFalls';
      falls.renderOrder = 2;
      this.falls = falls;
      this.group.add(falls);
      this.disposables.push(fallGeo, fallMat);
    }
    if (M.idx.length) {
      const mistGeo = new THREE.BufferGeometry();
      mistGeo.setAttribute('position', new THREE.Float32BufferAttribute(M.pos, 3));
      mistGeo.setAttribute('aCorner', new THREE.Float32BufferAttribute(M.corner, 2));
      mistGeo.setAttribute('aM', new THREE.Float32BufferAttribute(M.m, 4));
      mistGeo.setAttribute('aDir', new THREE.Float32BufferAttribute(M.dir, 3));
      mistGeo.setIndex(M.idx);
      // billboards grow and drift in the shader: pad the bounds so culling never clips them
      mistGeo.computeBoundingSphere();
      if (mistGeo.boundingSphere) mistGeo.boundingSphere.radius += 30;
      const mistMat = this.shader(MIST_VERT, MIST_FRAG, shared, 'waterMist', { transparent: true, depthWrite: false });
      const mist = new THREE.Mesh(mistGeo, mistMat);
      mist.name = 'waterMist';
      mist.renderOrder = 3;
      this.group.add(mist);
      this.disposables.push(mistGeo, mistMat);
    }
  }

  /** Horizontal distance from a cascade's lip to the first stream point at or below its foot. */
  private cascadeFoot(world: World, x: number, z: number, y0: number): number {
    for (const s of world.streams) {
      const segs = s.segs;
      for (let i = 0; i < segs.length; i++) {
        const g = segs[i];
        if (Math.abs(g.ax - x) > 0.05 || Math.abs(g.az - z) > 0.05) continue;
        for (let k = i; k < segs.length; k++) {
          if (segs[k].yb <= y0 + 0.05) return len2(segs[k].bx - x, segs[k].bz - z);
        }
      }
    }
    return -1;
  }

  /** One stream run as a ribbon: cross-sections at every joint, mitred, sunk under level pools. */
  private ribbon(S: Buf, run: StreamSeg[], across: number[], sink: (x: number, z: number, y: number) => number) {
    const cols = across.length;
    const base = S.pos.length / 3;
    let along = 0;
    const n = run.length;
    for (let i = 0; i <= n; i++) {
      const g = run[Math.min(i, n - 1)];
      const prev = run[Math.max(i - 1, 0)];
      const x = i < n ? g.ax : g.bx, z = i < n ? g.az : g.bz, y = i < n ? g.ya : g.yb;
      // joint direction: average of the two segments that meet here
      let dx = prev.ux + g.ux, dz = prev.uz + g.uz;
      const dl = len2(dx, dz) || 1; dx /= dl; dz /= dl;
      const miter = Math.min(1.5, 1 / Math.max(0.3, dx * g.ux + dz * g.uz));
      const nx = -dz, nz = dx;
      // speed from the slope; froth where it drops fast (rapids and cascades)
      const slope = Math.max((prev.ya - prev.yb) / prev.len, (g.ya - g.yb) / g.len);
      const spd = 0.9 + Math.min(4, slope * 30);
      const froth = Math.min(1, Math.max(0, (slope - 0.09) * 2.2));
      for (let c = 0; c < cols; c++) {
        const a = across[c];
        const off = a * g.hw * miter;
        const vx = x + nx * off, vz = z + nz * off;
        const vy = sink(vx, vz, y);
        S.pos.push(vx, vy, vz);
        S.uv.push(a * g.hw, along);
        S.p.push(Math.abs(a), spd, froth, 1); S.d.push(0, 0, 0, 0);
      }
      if (i < n) along += g.len;
    }
    for (let i = 0; i < n; i++) for (let c = 0; c < cols - 1; c++) {
      // a run that drops off a cliff is drawn by its fall sheet, not as a tilted ribbon
      if (run[i].ya - run[i].yb > 1.0) continue;
      const a = base + i * cols + c, b = a + 1, d = a + cols, e = d + 1;
      S.idx.push(a, b, d, b, e, d);
    }
  }

  private shader(vert: string, frag: string, shared: Record<string, THREE.IUniform>, key: string, o: { transparent: boolean; depthWrite: boolean; side?: THREE.Side }, fogMax = 1) {
    const m = new THREE.ShaderMaterial({
      vertexShader: vert,
      fragmentShader: frag,
      uniforms: { ...shared },
      transparent: o.transparent,
      depthWrite: o.depthWrite,
      side: o.side ?? THREE.FrontSide,
      defines: { WQ: this.q },
    });
    // withHaze adds vHazeWorld + applyHaze (via <project_vertex> / <fog_fragment>) and every
    // HAZE uniform, uTime and uSunDir included
    withHaze(m, fogMax);
    m.customProgramCacheKey = () => key + this.q;
    return m;
  }

  update(time: number, camPos: THREE.Vector3): void { void time; void camPos; }

  /**
   * What the still water mirrors (world/index.ts hands over the island tops, strata, landforms,
   * town, foliage and sky groups). Only their solid meshes are tagged for the mirror camera;
   * grass, particles, motes, birds, mist and anything added later by fx/enemies never are.
   */
  setReflectCasters(roots: THREE.Object3D[]): void {
    if (!this.refl) return;
    this.refl.setCasters(this.falls ? [...roots, this.falls] : roots);
  }

  /** last mirror pass: draw calls, triangles, whether it ran, which body (lab/budget readout) */
  get reflectStats() { return this.refl?.stats ?? null; }

  dispose() { for (const d of this.disposables) d.dispose(); }
}
